// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * API endpoint for retrieving files associated with assets.
 * Provides functionality to list all documents, images, and other files
 * that have been attached to a specific asset.
 */
import { api } from '@/api/axios-instance';
import { parseResponse } from '@/api/parse-response';
import { responseMetadataSchema } from '@/api/types';
import { fileSchema } from '@/file/api/types';
import { z } from 'zod';

/**
 * Schema for validating the API response when fetching asset files.
 * Transforms the response to provide a more intuitive structure with
 * the files array separated from metadata.
 */
const assetFilesResponseSchema = z.object({
  data: z.array(fileSchema),
  metadata: responseMetadataSchema,
});

/**
 * Retrieves all files associated with a specific asset.
 * This function handles the API call to fetch file metadata and validates
 * the response data using Zod schemas.
 *
 * @param key - Unique identifier of the asset to fetch files for
 * @returns Promise resolving to the list of files and request metadata
 * @throws Will throw an error if the API request fails or if the response data is invalid
 */
export async function getAssetFiles(key: string) {
  const path = `/v1/asset/${key}/file`;
  const response = await api.get(path);

  return parseResponse(assetFilesResponseSchema, response.data, {
    schemaName: 'AssetFiles',
    endpoint: path,
  });
}
